import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { FiCopy } from "react-icons/fi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCode, faEye, faPalette } from "@fortawesome/free-solid-svg-icons";
import { fetchRestaurantDetails } from "../actions/restaurantActions";
import Loader from "../components/Loader";
import Messages from "../components/Messages";
import { ModernButton } from "../components/DefaultImports";
const MOBILE_APP_HEADER_HEIGHT = 60;
const PREVIEW_SIZES = {
  small: {
    label: "Pequeno",
    width: 360,
    height: 450
  },
  medium: {
    label: "Médio",
    width: 500,
    height: 600
  },
  large: {
    label: "Grande",
    width: 720,
    height: 760
  }
};
const Page = styled.div`
  width: 100%;
  height: 100dvh;
  max-height: 100dvh;
  min-height: 0;
  margin: 0;
  padding: 24px;
  box-sizing: border-box;
  overflow: hidden;
  background: #f7f8f8;

  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 14px;

  @media (max-width: 900px) {
    height: calc(100dvh - ${MOBILE_APP_HEADER_HEIGHT}px);
    max-height: calc(100dvh - ${MOBILE_APP_HEADER_HEIGHT}px);
    padding: 6px;
    gap: 6px;
  }
`;
const Header = styled.div`
  min-height: 44px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  overflow: visible;
  position: relative;
  z-index: 2;

  @media (max-width: 900px) {
    min-height: 34px;
    gap: 6px;

    button {
      min-height: 26px !important;
      padding: 4px 8px !important;
      font-size: 10px !important;
      white-space: nowrap;
    }

    svg {
      margin-right: 4px !important;
    }
  }
`;
const TitleBlock = styled.div`
  min-width: 0;
  overflow: hidden;
`;
const Title = styled.h2`
  margin: 0;
  color: #99b0ad;
  font-size: 1.35rem;
  font-weight: 750;
  line-height: 1.15;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 900px) {
    font-size: 0.86rem;
  }
`;
const Subtitle = styled.p`
  margin: 4px 0 0;
  color: #6f7c79;
  font-size: 13px;

  @media (max-width: 900px) {
    display: none;
  }
`;
const Grid = styled.div`
  min-height: 0;
  height: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 14px;
  align-items: stretch;
  overflow: hidden;
  position: relative;
  z-index: 1;

  @media (max-width: 900px) {
    width: 100%;
    grid-template-columns: 1fr;
    grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
    gap: 6px;
  }
`;
const Card = styled.div`
  min-width: 0;
  min-height: 0;
  background: #ffffff;
  border: 1px solid rgba(153, 176, 173, 0.32);
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.04);
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;

  scrollbar-width: thin;
  scrollbar-color: rgba(153, 176, 173, 0.55) transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(153, 176, 173, 0.55);
    border-radius: 999px;
  }

  @media (max-width: 900px) {
    width: 100%;
    height: 100%;
    max-height: 100%;
    padding: 4px 2px;
    border: none;
    border-radius: 0;
    box-shadow: none;
    background: transparent;
  }
`;
const PreviewCard = styled(Card)`
  display: flex;
  flex-direction: column;
`;
const CardTitle = styled.h4`
  margin: 0 0 10px;
  color: #4f5f5b;
  font-size: 1rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;

  @media (max-width: 900px) {
    margin: 0 0 4px;
    font-size: 0.78rem;
    gap: 4px;
    line-height: 1.1;
  }
`;
const Field = styled.div`
  margin-bottom: 10px;

  @media (max-width: 900px) {
    margin-bottom: 6px;
  }
`;
const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  color: #4f5f5b;
  font-size: 13px;
  font-weight: 650;

  @media (max-width: 900px) {
    margin-bottom: 3px;
    font-size: 11px;
  }
`;
const ReadOnlyText = styled.div`
  width: 100%;
  min-height: 36px;
  padding: 8px 10px;
  border-radius: 9px;
  border: 1px solid #d8dddb;
  background: #fafafa;
  color: #343a38;
  font-size: 13px;
  font-weight: 650;
  box-sizing: border-box;

  @media (max-width: 900px) {
    min-height: 28px;
    padding: 6px 8px;
    border-radius: 7px;
    font-size: 11px;
  }
`;
const Input = styled.input`
  width: 100%;
  height: 36px;
  padding: 0 10px;
  border-radius: 9px;
  border: 1px solid #d8dddb;
  background: #fafafa;
  color: #343a38;
  font-size: 13px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #ee754c;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(238, 117, 76, 0.13);
  }

  @media (max-width: 900px) {
    height: 28px;
    padding: 0 8px;
    border-radius: 7px;
    font-size: 11px;
  }
`;
const ColorRow = styled.div`
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr);
  gap: 8px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 30px minmax(0, 1fr);
    gap: 6px;
  }
`;
const ColorInput = styled.input`
  width: 38px;
  height: 36px;
  padding: 0;
  border-radius: 9px;
  border: 1px solid #d8dddb;
  background: transparent;
  cursor: pointer;
  overflow: hidden;
  box-sizing: border-box;
  appearance: none;
  -webkit-appearance: none;

  &::-webkit-color-swatch-wrapper {
    padding: 0;
    border: none;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 8px;
  }

  &::-moz-color-swatch {
    border: none;
    border-radius: 8px;
  }

  @media (max-width: 900px) {
    width: 30px;
    height: 28px;
    border-radius: 7px;

    &::-webkit-color-swatch {
      border-radius: 6px;
    }

    &::-moz-color-swatch {
      border-radius: 6px;
    }
  }
`;
const BackgroundChoiceRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  @media (max-width: 900px) {
    gap: 6px;
  }
`;
const BackgroundChoiceButton = styled.button`
  height: 36px;
  border-radius: 999px;
  border: 1px solid
    ${({
  $active,
  $mainColor
}) => $active ? $mainColor : "#d8dddb"};
  background: ${({
  $active,
  $mainColor,
  $bg
}) => $active ? $mainColor : $bg};
  color: ${({
  $active,
  $dark
}) => $active ? "#ffffff" : $dark ? "#ffffff" : "#4f5f5b"};
  font-size: 13px;
  font-weight: 750;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    border-color: ${({
  $mainColor
}) => $mainColor};
  }

  @media (max-width: 900px) {
    height: 28px;
    font-size: 11px;
  }
`;
const CodeBox = styled.pre`
  width: 100%;
  max-height: 150px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0 0 10px;
  padding: 10px;
  border-radius: 9px;
  border: 1px solid #d8dddb;
  background: #f7f7f7;
  color: #333;
  font-size: 12px;

  @media (max-width: 900px) {
    max-height: 54px;
    margin-bottom: 5px;
    padding: 6px;
    border-radius: 7px;
    font-size: 9px;
  }
`;
const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (max-width: 900px) {
    gap: 5px;

    button {
      min-height: 26px !important;
      padding: 4px 8px !important;
      font-size: 10px !important;
    }
  }
`;
const PreviewSizeRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;

  @media (max-width: 900px) {
    display: none;
  }
`;
const PreviewSizeButton = styled.button`
  border: 1px solid
    ${({
  $active,
  $mainColor
}) => $active ? $mainColor : "#d8dddb"};
  background: ${({
  $active,
  $mainColor
}) => $active ? $mainColor : "#ffffff"};
  color: ${({
  $active
}) => $active ? "#ffffff" : "#4f5f5b"};
  border-radius: 999px;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: ${({
  $mainColor
}) => $mainColor};
  }
`;
const PreviewOuter = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  overflow: auto;
  padding-bottom: 6px;

  @media (max-width: 900px) {
    height: 100%;
    min-height: 0;
    flex: 1;
    overflow: auto;
    padding-bottom: 0;
  }
`;
const PreviewFrameWrap = styled.div`
  width: ${({
  $width
}) => `${$width}px`};
  max-width: 100%;
  height: ${({
  $height
}) => `${$height}px`};
  overflow: hidden;
  border-radius: ${({
  $radius
}) => `${$radius}px`};
  background: ${({
  $backgroundColor
}) => $backgroundColor};
  border: 1px solid #99b0ad;

  @media (max-width: 900px) {
    width: 100%;
    height: 100%;
    min-height: 0;
    border: none;
    border-radius: ${({
  $radius
}) => `${$radius}px`};
  }
`;
const PreviewFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
`;
const HelpText = styled.p`
  margin: 5px 0 0;
  color: #7c8885;
  font-size: 11px;
  line-height: 1.35;

  @media (max-width: 900px) {
    display: none;
  }
`;
const isHexColor = value => /^#[0-9A-Fa-f]{6}$/.test(value || "");
function RestaurantWidgetSettings() {
  const {
    t
  } = useTranslation();
  const {
    restaurant_id
  } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const restaurantDetails = useSelector(state => state.restaurantDetails);
  const {
    restaurants,
    isLoading,
    error
  } = restaurantDetails;
  const [mainColor, setMainColor] = useState("#EE754C");
  const [backgroundColor, setBackgroundColor] = useState("#FFFFFF");
  const [borderRadius, setBorderRadius] = useState(12);
  const [previewSize, setPreviewSize] = useState("medium");
  useEffect(() => {
    if (restaurant_id) {
      dispatch(fetchRestaurantDetails(restaurant_id));
    }
  }, [dispatch, restaurant_id]);
  const selectedPreview = PREVIEW_SIZES[previewSize];
  const autoWidgetTitle = useMemo(() => {
    const restaurantName = restaurants?.restaurant_name?.trim();
    if (!restaurantName) {
      return t("ui.i18nFinalCleanup.bookTable");
    }
    return `Reservar no ${restaurantName}`;
  }, [restaurants]);
  const widgetUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("title", autoWidgetTitle);
    params.set("showName", "0");
    params.set("main", mainColor || "#EE754C");
    params.set("bg", backgroundColor || "#FFFFFF");
    params.set("radius", String(borderRadius || 12));
    return `${window.location.origin}/#/widget/reservations/${restaurant_id}?${params.toString()}`;
  }, [restaurant_id, autoWidgetTitle, mainColor, backgroundColor, borderRadius]);
  const iframeCode = useMemo(() => {
    return `<iframe
  src="${widgetUrl}"
  style="width:100%; height:100%; border:0; border-radius:${borderRadius}px; display:block;"
  loading="lazy"
></iframe>`;
  }, [widgetUrl, borderRadius]);
  const copySuccess = message => {
    toast(message, {
      autoClose: 1200
    });
  };
  return <Page>
      <ToastContainer />

      <Header>
        <TitleBlock>
          <Title>
            <FontAwesomeIcon icon={faCode} style={{
            marginRight: "8px"
          }} />{t("ui.screens.restaurantWidgetSettings.widgetDeReservas")}</Title>
          <Subtitle>
            {restaurants?.restaurant_name ? `Configura e copia o widget de ${restaurants.restaurant_name}.` : t("ui.i18nCleanup2.configuraECopiaOWidgetParaOSiteDoRestaurante")}
          </Subtitle>
        </TitleBlock>

        <ModernButton color="#99B0AD" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} style={{
          marginRight: "6px"
        }} />{t("ui.screens.restaurantWidgetSettings.voltar")}</ModernButton>
      </Header>

      {isLoading ? <Loader /> : error ? <Messages variant="danger">{error}</Messages> : <Grid>
          <Card>
            <CardTitle>
              <FontAwesomeIcon icon={faPalette} />{t("ui.screens.restaurantWidgetSettings.personalizacao")}</CardTitle>

            <Field>
              <Label>{t("ui.screens.restaurantWidgetSettings.tituloAutomatico")}</Label>
              <ReadOnlyText>{autoWidgetTitle}</ReadOnlyText>
            </Field>

            <Field>
              <Label>{t("ui.screens.restaurantWidgetSettings.corPrincipal")}</Label>
              <ColorRow>
                <ColorInput type="color" value={isHexColor(mainColor) ? mainColor : "#EE754C"} onChange={event => setMainColor(event.target.value)} />
                <Input type="text" value={mainColor} onChange={event => setMainColor(event.target.value)} placeholder={t("ui.screens.restaurantWidgetSettings.ee754cOuRgb23811776")} />
              </ColorRow>
            </Field>

            <Field>
              <Label>{t("ui.screens.restaurantWidgetSettings.fundo")}</Label>
              <BackgroundChoiceRow>
                <BackgroundChoiceButton type="button" $active={backgroundColor === "#FFFFFF"} $mainColor={mainColor} $bg="#FFFFFF" onClick={() => setBackgroundColor("#FFFFFF")}>{t("ui.screens.restaurantWidgetSettings.claro")}</BackgroundChoiceButton>

                <BackgroundChoiceButton type="button" $active={backgroundColor === "#2F3437"} $mainColor={mainColor} $bg="#2F3437" $dark onClick={() => setBackgroundColor("#2F3437")}>{t("ui.screens.restaurantWidgetSettings.escuro")}</BackgroundChoiceButton>
              </BackgroundChoiceRow>
            </Field>

            <Field>
              <Label>{t("ui.screens.restaurantWidgetSettings.cantos")}</Label>
              <Input type="number" min="0" max="40" value={borderRadius} onChange={event => setBorderRadius(event.target.value)} />
            </Field>

            <Field>
              <Label>{t("ui.screens.restaurantWidgetSettings.linkDireto")}</Label>
              <CodeBox>{widgetUrl}</CodeBox>

              <ButtonRow>
                <CopyToClipboard text={widgetUrl} onCopy={() => copySuccess(t("ui.i18nFinalCleanup.widgetLinkCopied"))}>
                  <ModernButton color="#FAF6E9" style={{
                color: "#EE754C"
              }}>
                    <FiCopy style={{
                  marginRight: "6px"
                }} />{t("ui.screens.restaurantWidgetSettings.copiarLink")}</ModernButton>
                </CopyToClipboard>
              </ButtonRow>
            </Field>

            <Field>
              <Label>{t("ui.screens.restaurantWidgetSettings.codigoParaOSite")}</Label>
              <CodeBox>{iframeCode}</CodeBox>
              <HelpText>{t("ui.screens.restaurantWidgetSettings.oIframeUsaAltura100A")}</HelpText>

              <ButtonRow>
                <CopyToClipboard text={iframeCode} onCopy={() => copySuccess(t("ui.i18nFinalCleanup.widgetCodeCopied"))}>
                  <ModernButton color="#EE754C">
                    <FiCopy style={{
                  marginRight: "6px"
                }} />{t("ui.screens.restaurantWidgetSettings.copiarCodigo")}</ModernButton>
                </CopyToClipboard>
              </ButtonRow>
            </Field>
          </Card>

          <PreviewCard>
            <CardTitle>
              <FontAwesomeIcon icon={faEye} />{t("ui.screens.restaurantWidgetSettings.preVisualizacao")}</CardTitle>

            <PreviewSizeRow>
              {Object.entries(PREVIEW_SIZES).map(([key, size]) => <PreviewSizeButton key={key} type="button" $active={previewSize === key} $mainColor={mainColor} onClick={() => setPreviewSize(key)}>
                  {size.label}
                </PreviewSizeButton>)}
            </PreviewSizeRow>

            <PreviewOuter>
              <PreviewFrameWrap $width={selectedPreview.width} $height={selectedPreview.height} $radius={borderRadius} $backgroundColor={backgroundColor}>
                <PreviewFrame key={widgetUrl} title={t("ui.screens.restaurantWidgetSettings.preVisualizacaoDoWidgetDeReservas")} src={widgetUrl} />
              </PreviewFrameWrap>
            </PreviewOuter>
          </PreviewCard>
        </Grid>}
    </Page>;
}
export default RestaurantWidgetSettings;
